import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Client-side axios: withCredentials so httpOnly cookies flow both ways.
// Timeout global généreux (90s) — au-delà, on assume que Cloudflare va aussi
// couper (limite tunnel ~100s) donc on abandonne côté client avec un message
// clair plutôt que d'attendre un HTML 520 illisible.
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 90_000,
});

// Intercepteur global : quand le backend refuse un appel car le rôle exige
// l'activation de la 2FA (403 avec code `two_factor_enrollment_required`),
// on redirige DIRECTEMENT vers la page d'enrôlement ET on marque l'erreur
// comme "silenced" pour que les callers ne montrent pas de toast rouge inutile.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    const code = (typeof detail === "object" && detail?.error)
                  || err?.response?.data?.error;
    if (code === "two_factor_enrollment_required") {
      err.__silenced = true;
      if (typeof window !== "undefined"
          && !window.location.pathname.startsWith("/app/settings/security")
          && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/app/settings/security?enroll=1");
      }
    }
    // Feature-not-included 403 → dispatch upsell event (global listener catches it)
    if (err?.response?.status === 403 && typeof detail === "string"
        && detail.startsWith("Fonctionnalité non incluse")) {
      const feature = detail.split(":").pop().trim();
      err.__silenced = true;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("clarix:upsell", { detail: { feature } }));
      }
    }
    return Promise.reject(err);
  },
);

const EN_DETAILS = {
  "not found": "Élément introuvable (il a peut-être été supprimé).",
  "forbidden": "Accès refusé : ton rôle ne permet pas cette action.",
  "not authenticated": "Session expirée — reconnecte-toi.",
  "unauthorized": "Session expirée — reconnecte-toi.",
  "internal server error": "Erreur interne du serveur.",
  "method not allowed": "Action non disponible.",
  "bad request": "Requête invalide.",
};

const STATUS_MESSAGES = {
  400: "Requête invalide : vérifie les informations saisies.",
  401: "Session expirée — reconnecte-toi.",
  403: "Accès refusé : ton rôle ou ton offre ne permet pas cette action.",
  404: "Élément introuvable (il a peut-être été supprimé).",
  409: "Action impossible dans l'état actuel (conflit avec une donnée existante).",
  413: "Fichier trop volumineux.",
  422: "Données invalides ou incomplètes.",
  429: "Trop de demandes en peu de temps — patiente quelques secondes.",
  500: "Erreur interne du serveur.",
  502: "Le service IA ou un service externe n'a pas répondu.",
  503: "Service momentanément indisponible.",
};

const AREA_LABELS = {
  legal: "Droit des sociétés", documents: "coffre-fort", folders: "dossiers du coffre-fort", knowledge: "connaissances",
  registers: "registres", age: "AGE", ago: "AGO", assemblies: "assemblées", associes: "associés", dirigeants: "dirigeants",
  profile: "profil société", import: "import de documents", "alert-settings": "réglages des alertes", alerts: "alertes",
  statuts: "statuts", templates: "modèles", chat: "assistant IA", "ai-intent": "assistant IA", hub: "accueil",
  "weekly-digest": "récap hebdomadaire", "conformity-score": "score de conformité", onboarding: "mise en route", governance: "gouvernance", ocr: "lecture IA", branding: "identité visuelle",
};

function actionContext(e) {
  const cfg = e?.config;
  if (!cfg?.url) return "";
  const path = cfg.url.replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "").split("?")[0];
  const segs = path.split("/").filter(Boolean);
  const area = segs.slice(1).map((s) => AREA_LABELS[s]).find(Boolean) || AREA_LABELS[segs[0]];
  const verb = (cfg.method || "get").toLowerCase() === "get" ? "Chargement" : (cfg.method === "delete" ? "Suppression" : "Enregistrement");
  return area ? `${verb} « ${area} »` : verb;
}

// Global response formatting for FastAPI 422 detail arrays.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return EN_DETAILS[detail.trim().toLowerCase()] || detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Format d'erreur Averdy Agency { error: "code", message: "texte FR" } — on
  // affiche la version française, pas le JSON brut.
  if (detail && typeof detail === "object"
      && typeof detail.message === "string") return detail.message;
  // Objet complexe (schéma d'erreur non-standard) — JSON.stringify évite le
  // `[object Object]` inutile qui trompait l'utilisateur.
  try {
    return JSON.stringify(detail);
  } catch {
    return "Une erreur est survenue.";
  }
}

export function apiError(e, fallback = "Une erreur est survenue.") {
  // Erreurs déjà traitées par l'intercepteur (ex : redirect 2FA enroll) —
  // le caller ne doit rien afficher, on renvoie une chaîne vide.
  if (e?.__silenced) return "";
  // Timeout / abort côté client
  if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
    return "Le calcul IA prend plus de temps que prévu. Réessaie ou choisis un modèle plus rapide.";
  }
  // Cloudflare / origin 5xx : le body est du HTML illisible, on masque et on
  // remonte un message FR propre plutôt qu'un dump HTML dans un toast.
  const status = e?.response?.status;
  const raw = e?.response?.data;
  const looksLikeHtml = typeof raw === "string"
    && (raw.trimStart().startsWith("<") || raw.includes("Cloudflare"));
  if (status && status >= 500) {
    if (status === 520 || status === 521 || status === 522 || status === 524) {
      return "Le serveur IA a mis trop de temps à répondre. Réessaie dans un instant.";
    }
    if (looksLikeHtml) {
      return "Erreur serveur temporaire — réessaie dans un instant.";
    }
  }
  if (looksLikeHtml) {
    return "Réponse inattendue du serveur — réessaie.";
  }
  const ctx = actionContext(e);
  if (e?.request && !e?.response) {
    return `${ctx ? ctx + " : " : ""}impossible de joindre le serveur — vérifie ta connexion internet.`;
  }
  const detail = typeof raw === "object" && !(raw instanceof Blob) ? raw?.detail : null;
  const msg = formatApiErrorDetail(detail) || STATUS_MESSAGES[status] || (status ? `Erreur inattendue (code ${status}).` : e?.message) || fallback;
  const apiPath = (e?.config?.url || "").replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "").split("?")[0];
  const suffix = status && status >= 500 ? ` (code ${status}${apiPath ? ` · ${apiPath}` : ""})` : "";
  return ctx ? `${ctx} : ${msg}${suffix}` : `${msg}${suffix}`;
}

export default api;
